import { useState } from "react"
import Image from "components/Image"
import Link from "next/link"
import tw, { css, styled } from "twin.macro"

import useLFL from "lib/useLFL"
import { LinkButtonProps } from "lib/types"
import { breakpoint } from "styles/Variables"
import { BodyDetail, H1, H6, IntroBody } from "styles/Text"

import MetaData from "components/Metadata"
import TransparentOverlay from "components/Heroes/TransparentOverlay"
import Container from "components/Container"
import WeatherWidget from "components/WeatherWidget"
import Modal from "components/Modal"
import GalleryButton from "components/Buttons/GalleryButton"
import PrimaryButton from "components/Buttons/Primary"
import Grid from "components/Grid"
import Map from "components/Map"
import Column from "components/Column"
import Gallery from "components/Carousels/Gallery"
import Language from "components/Language"

export interface HeroLocationProps {
  id: string
  BackgroundImage: {
    alternativeText: string
    url: string
  }
  ImageFocusHorizontal?: "left" | "center" | "right"
  ImageFocusVertical?: "top" | "center" | "bottom"
  Title: string
  Description: string
  Latitude: number
  Longitude: number
  OpeningHours: string
  Accessible: string
  Link: LinkButtonProps
  Gallery: { id: string | number; alternativeText: string; url: string }[]
  LFLNameID: string
  WeatherData: {
    typeId: string
    type: string
    temp: string | number
  }
}

export const HeroLocationQuery = {
  component: "ComponentHeroLocation",
  fields: `{
    id
    BackgroundImage {
      alternativeText
      url
    }
    ImageFocusHorizontal
    ImageFocusVertical
    Title
    Description
    Latitude
    Longitude
    OpeningHours
    Accessible
    Gallery {
      id
      alternativeText
      url
    }
    Link {
      Url
      Title
    }
  }`,
}

const HeroLocationContent = styled.div(() => [
  tw`z-10 relative h-full flex flex-col justify-center items-center w-full`,
  css`
    min-height: 720px;
    ${breakpoint.md`
      min-height: 800px;
    `}
  `,
])

const HeroLocation = (props: HeroLocationProps) => {
  const {
    Title,
    Description,
    BackgroundImage,
    Latitude,
    Longitude,
    WeatherData,
    OpeningHours,
    Accessible,
    Gallery: GalleryData,
    Link: LinkButton,
    LFLNameID,
    ImageFocusHorizontal = "center",
    ImageFocusVertical = "center",
  } = props

  const { replacedText, ...LFLData } = useLFL(LFLNameID)

  const [modalOpen, setModalOpen] = useState(false)

  return (
    <header tw="bg-black text-white flex flex-col items-center justify-center">
      <MetaData Title={Title} Description={Description} />

      <div tw="relative text-center w-full">
        {BackgroundImage && (
          <Image
            aria-hidden="true"
            alt={BackgroundImage.alternativeText}
            src={BackgroundImage.url}
            layout="fill"
            objectFit="cover"
            objectPosition={[ImageFocusHorizontal, ImageFocusVertical].join(
              " "
            )}
            priority
          />
        )}

        <Container>
          <HeroLocationContent>
            {WeatherData.type && (
              <WeatherWidget
                temp={WeatherData.temp}
                type={WeatherData.type}
                typeId={WeatherData.typeId}
              />
            )}
            <Grid halign="center" wrap tw="w-full">
              <Column size={{ md: 2 / 3 }}>
                <H1 tw="mt-5 md:mt-6">
                  {replacedText || LFLData?.markedup_text || Title}
                </H1>
              </Column>

              <Column size={{ md: 1 / 2 }}>
                <IntroBody tw="mt-5 mx-auto max-w-xs md:max-w-2xl">
                  <Language unwrap>{Description}</Language>
                </IntroBody>
              </Column>
            </Grid>

            {GalleryData.length > 0 && (
              <div tw="absolute left-5 bottom-5 z-10">
                <GalleryButton onClick={() => setModalOpen(true)} />
              </div>
            )}
          </HeroLocationContent>
        </Container>

        <TransparentOverlay />

        {GalleryData.length > 0 && modalOpen && (
          <Modal removeBox isOpen={modalOpen} close={() => setModalOpen(false)}>
            <Gallery galleryData={GalleryData} />
          </Modal>
        )}
      </div>

      <div tw="relative bg-earth mt-auto w-full max-w-full">
        <Container tw="relative py-11 max-w-full">
          <Grid halign={{ xs: "center", lg: "right" }} wrap={{ md: true }}>
            <Column size={{ md: 1 / 3 }}>
              <H6 as="h2">OPENING TIMES</H6>
              <BodyDetail tw="mt-2.5">{OpeningHours}</BodyDetail>
            </Column>

            <Column tw="mt-6 md:mt-0" size={{ md: 1 / 3 }}>
              <H6 as="h2">ACCESSIBLE</H6>
              <BodyDetail tw="mt-2.5">{Accessible}</BodyDetail>
            </Column>
            <Column tw="text-center" size={{ lg: 1 / 6 }}>
              <div tw="absolute top-0 right-5 -mt-10 lg:-mt-30 lg:static">
                {Longitude && Latitude && (
                  <Map
                    lng={Longitude}
                    lat={Latitude}
                    bunubaOverlay={false}
                    pin
                  />
                )}
              </div>

              {LinkButton?.Url && (
                <Link href={LinkButton.Url} passHref>
                  <PrimaryButton
                    rel="nofollow noopener"
                    target="_blank"
                    as="a"
                    tw="mt-7.5"
                  >
                    {LinkButton.Title}
                  </PrimaryButton>
                </Link>
              )}
            </Column>
          </Grid>
        </Container>
      </div>
    </header>
  )
}

export default HeroLocation
